<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <div class="dash-header">
              <div>
                <h2>IABF {{ adminFestivalYear }} — Dancers</h2>
                <p v-if="isArchiveYear">Archive roster. Remove someone if they should not count for this year. Their other years stay.</p>
                <p v-else>Get ready to have fun!</p>
              </div>
              <div v-if="isOperableYear" class="dash-actions">
                <v-btn small color="primary" dark to="/iabf/admin/dancers/new">+ New dancer</v-btn>
                <v-btn small outlined @click="downloadRoomingList">↓ Rooming list (CSV)</v-btn>
                <v-btn small outlined @click="downloadNametagsCsv">↓ Nametags (CSV)</v-btn>
                <v-btn small outlined @click="openWelcomeCards">Welcome cards</v-btn>
              </div>
            </div>
            <div v-if="rows.length" class="roster-stats">
              <p class="roster-stat-line">
                <span class="roster-stat-label">Rooms</span>
                {{ roomTypeTally }}
              </p>
              <p class="roster-stat-line">
                <span class="roster-stat-label">Years</span>
                {{ yearCohortTally }}
              </p>
            </div>
            <div class="copy-emails">
              <h3>Copy emails</h3>
              <p class="copy-hint">
                One per line — good for BCC.
                {{ dancerEmails.length }} dancer{{ dancerEmails.length === 1 ? '' : 's' }},
                {{ facultyEmails.length }} faculty.
                Onsite and offsite include faculty.
                Onsite is dorms, cottages, and anyone sharing an onsite room.
                Offsite is anyone staying nearby on their own.
              </p>
              <div class="dash-actions">
                <v-btn
                  small
                  outlined
                  :disabled="!dancerEmails.length"
                  @click="copyRosterEmails('dancers')"
                >
                  Copy dancer emails
                </v-btn>
                <v-btn
                  small
                  outlined
                  :disabled="!facultyEmails.length"
                  @click="copyRosterEmails('faculty')"
                >
                  Copy faculty emails
                </v-btn>
                <v-btn
                  small
                  outlined
                  :disabled="!allRosterEmails.length"
                  @click="copyRosterEmails('both')"
                >
                  Copy dancers + faculty
                </v-btn>
                <v-btn
                  small
                  outlined
                  :disabled="!onsiteEmails.length"
                  @click="copyRosterEmails('onsite')"
                >
                  Copy onsite emails
                </v-btn>
                <v-btn
                  small
                  outlined
                  :disabled="!offsiteEmails.length"
                  @click="copyRosterEmails('offsite')"
                >
                  Copy offsite emails
                </v-btn>
                <span v-if="emailsCopyNote" class="copy-feedback" role="status">{{ emailsCopyNote }}</span>
              </div>
            </div>
            <hr />
            <vue-good-table
              :columns="displayColumns"
              :rows="rows"
              :line-numbers="true"
              :search-options="{
                enabled: true,
                placeholder: 'Search dancers',
              }"
            >
              <div slot="emptystate">
                No dancers match your search!
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field =='edit' && isOperableYear">
                  <v-btn
                    icon
                    :to="'/iabf/admin/dancers/edit/' + props.row.id"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </span>
                <span v-else-if="props.column.field == 'emailIdentity'">
                  <v-btn
                    icon
                    title="Link emails"
                    @click="openEmailIdentity(props.row)"
                  >
                    <v-icon>mdi-email</v-icon>
                  </v-btn>
                </span>
                <span v-if="props.column.field == 'account' && isOperableYear">
                  <v-icon style="cursor:pointer" @click="masqueradeAs(props.row.id)">mdi-account</v-icon>
                </span>
                <span v-else-if="props.column.field == 'bucks' && props.formattedRow[props.column.field] > 0">
                  ${{ props.formattedRow[props.column.field] }}
                </span>
                <span v-else-if="props.column.field == 'initialPayment'">
                  ${{ props.formattedRow[props.column.field] }}
                </span>
                <span v-else-if="props.column.field == 'payment1Due'">
                  {{ payment1Display(props.formattedRow) }}
                </span>
                <span v-else-if="props.column.field == 'payment2Due'">
                  {{ payment2Display(props.formattedRow) }}
                </span>
                <span v-else-if="props.column.field == 'payment3Due'">
                  {{ payment3Display(props.formattedRow) }}
                </span>
                <span v-else-if="props.column.field == 'payment4Due'">
                  {{ payment4Display(props.formattedRow) }}
                </span>
                <span v-else-if="props.column.field == 'payment5Due'">
                  {{ payment5Display(props.formattedRow) }}
                </span>
                <span v-else-if="props.column.field == 'payment6Due'">
                  <span v-if="props.row.outstandingBalance > 0" style="background: #fffde7; color: #5d4037; font-weight: bold; padding: 2px 5px; border-radius: 3px; white-space: nowrap;">
                    <span v-if="payment6Display(props.formattedRow)">{{ payment6Display(props.formattedRow) }} + </span>${{ props.row.outstandingBalance }} outstanding
                  </span>
                  <span v-else>{{ payment6Display(props.formattedRow) }}</span>
                </span>
                <span v-else-if="props.column.field === 'costumeStatus'">
                  <span :style="props.row.costumeStatus === 'Yes - Paid' ? 'color: green; font-weight: bold' : props.row.costumeStatus === 'Yes - Unpaid' ? 'color: orange; font-weight: bold' : 'color: grey'">
                    {{ props.row.costumeStatus }}
                  </span>
                </span>
                <span v-else-if="props.column.field === 'outstandingBalance' && props.row.outstandingBalance > 0">
                  <strong style="color: #c62828">${{ props.row.outstandingBalance }}</strong>
                </span>
                <span
                  v-else-if="props.column.field === 'yearsAttendedCount'"
                  :title="props.row.yearsAttendedLabel || 'No years on file'"
                >
                  {{ props.row.yearsAttendedCount || '—' }}
                </span>
                <span v-else-if="props.column.field === 'remove'">
                  <v-btn
                    small
                    text
                    color="red"
                    :loading="removingId === props.row.id"
                    :disabled="!!removingId"
                    @click="removeFromArchiveYear(props.row)"
                  >
                    Remove
                  </v-btn>
                </span>
                <span v-else-if="props.column.field === 'emailIdentity' || props.column.field === 'edit' || props.column.field === 'account'"></span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
            <div v-if="isOperableYear" class="onsite-rooms">
              <h3>Onsite room numbers</h3>
              <p class="copy-hint">
                Assign a number to each onsite room. People sharing a room appear together on the room holder’s row
                (they still see the number on their dashboard).
                Save to keep them and sort the list by room number.
                <span v-if="onsiteRoomingPeople.length">
                  {{ onsiteRoomsAssignedCount }} of {{ onsiteRoomingPeople.length }} assigned.
                </span>
              </p>
              <p v-if="!onsiteRoomingPeople.length" class="copy-hint">No onsite stays yet.</p>
              <table v-else class="onsite-rooms-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Room type</th>
                    <th>Room number</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="person in onsiteRoomingPeople" :key="person.key">
                    <td><strong>{{ person.name }}</strong></td>
                    <td>{{ person.role }}</td>
                    <td>{{ person.roomType }}</td>
                    <td>
                      <input
                        class="room-number-input"
                        type="text"
                        :value="roomNumberFor(person)"
                        :disabled="roomNumbersSaving"
                        placeholder="e.g. 12"
                        @input="onRoomNumberInput(person, $event)"
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="onsiteRoomingPeople.length" class="onsite-rooms-actions">
                <v-btn
                  small
                  color="primary"
                  dark
                  :disabled="!roomNumbersDirty || roomNumbersSaving"
                  :loading="roomNumbersSaving"
                  @click="saveRoomNumbers"
                >
                  Save room numbers
                </v-btn>
                <span v-if="roomNumbersNote" class="copy-feedback" role="status">{{ roomNumbersNote }}</span>
              </div>
            </div>
            <v-dialog v-model="emailIdentityOpen" width="560">
              <v-card v-if="emailIdentityRow">
                <v-card-title class="text-h5">
                  Link emails — {{ emailIdentityRow.firstName }} {{ emailIdentityRow.lastName }}
                </v-card-title>
                <v-card-text>
                  <iabf-person-email-identity
                    :key="emailIdentityPersonId + '|' + emailIdentityRow.email"
                    :person-id="emailIdentityPersonId"
                    :email="emailIdentityRow.email"
                    @updated="onEmailIdentityUpdated"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="emailIdentityOpen = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
    <div
      v-if="welcomeCardsOpen"
      class="welcome-cards-overlay"
    >
      <div class="welcome-cards-toolbar no-print">
        <strong>Welcome cards</strong>
        <span class="muted">{{ welcomeCardRows.length }} dancer{{ welcomeCardRows.length === 1 ? '' : 's' }} — name, address, years, pieces</span>
        <span class="welcome-cards-toolbar-actions">
          <v-btn small outlined :loading="welcomeCardsLoading" @click="openWelcomeCards">Refresh pieces</v-btn>
          <v-btn small outlined @click="downloadWelcomeCardsCsv">↓ CSV</v-btn>
          <v-btn small color="primary" dark @click="printWelcomeCards">Print</v-btn>
          <v-btn small outlined @click="welcomeCardsOpen = false">Close</v-btn>
        </span>
      </div>
      <p v-if="welcomeCardsLoading" class="welcome-cards-loading no-print">Loading this year’s pieces…</p>
      <div class="welcome-cards-sheet">
        <article
          v-for="card in welcomeCardRows"
          :key="card.id"
          class="welcome-card"
        >
          <p class="welcome-card-name">{{ card.name }}</p>
          <p
            v-for="(line, i) in card.addressLines"
            :key="i"
            class="welcome-card-address"
          >{{ line }}</p>
          <p v-if="!card.addressLines.length" class="welcome-card-missing">No mailing address on file</p>
          <p class="welcome-card-note">{{ card.yearsLine }}</p>
          <p v-if="!card.pieceNames.length" class="welcome-card-note">No pieces registered yet</p>
          <ul v-else class="welcome-card-pieces">
            <li v-for="(piece, pi) in card.pieceNames" :key="pi">{{ piece }}</li>
          </ul>
          <p v-if="card.priorGuestNames.length" class="welcome-card-note">Last year meals</p>
          <ul v-if="card.priorGuestNames.length" class="welcome-card-pieces">
            <li v-for="(guest, gi) in card.priorGuestNames" :key="'g' + gi">{{ guest }}</li>
          </ul>
        </article>
      </div>
    </div>
  </v-container>
</template>

<style>
  @media (min-width: 701px) {
    .mobile {
      display: none;
    }
  }
  .hidden {
    display:none;
  }

  .theme--light.v-btn {
    color: #267E7D;
  }

  .vgt-table, v-btn {
    font-family: Fanwood Text;
  }

  .vgt-table thead {
    font-size: 1.2em !important;
  }

  .vgt-row-header {
    color: #606266;
    font-size: 22px;
  }

  .clickable {
    cursor:pointer;
  }

  a.clickable {
    color: #6acecd;
    text-decoration: underline;
  }

  h2 {
    font-family: Quattrocento;
    font-weight: 400;
    font-style: normal;
    font-size: 39px;
    letter-spacing: -.02em;
    line-height: 1em;
    text-transform: none;
  }

  p, li {
    font-family: Fanwood Text;
    font-weight: 400;
    font-style: normal;
    font-size: 19px;
    letter-spacing: .01em;
    line-height: 1.7em;
    text-transform: none;
    color: rgba(40,26,23,.6);
    line-height: 1.7em;
    color: rgba(40,26,23,.6);
  }

  hr {
    margin: 12px 0;
  }

  .dash-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
  }

  .roster-stats {
    margin: 8px 0 16px;
    padding: 10px 14px;
    background: #f7f6f4;
    border: 1px solid #eceae6;
    border-radius: 8px;
  }

  .roster-stat-line {
    margin: 0;
    font-size: 15px;
    line-height: 1.45;
    color: #3d3a42;
  }

  .roster-stat-line + .roster-stat-line {
    margin-top: 4px;
  }

  .roster-stat-label {
    display: inline-block;
    min-width: 52px;
    font-weight: 600;
    color: #6b6570;
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .dash-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 8px;
  }

  .onsite-rooms {
    margin-top: 28px;
    padding-top: 16px;
    border-top: 1px solid #e3e0e8;
  }

  .copy-emails {
    margin: 16px 0 8px;
  }

  .onsite-rooms h3,
  .copy-emails h3 {
    font-family: Quattrocento, Georgia, serif;
    font-size: 22px;
    font-weight: 400;
    margin: 0 0 4px;
  }

  .onsite-rooms-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
  }

  .onsite-rooms-table th,
  .onsite-rooms-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    font-family: Fanwood Text, Georgia, serif;
    font-size: 16px;
  }

  .onsite-rooms-actions {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .room-number-input {
    width: 110px;
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font: inherit;
  }

  .onsite-rooms .copy-hint,
  .copy-emails .copy-hint {
    font-size: 14px;
    margin: 0 0 8px;
  }

  .copy-feedback {
    font-size: 13px;
    color: #2f6b4f;
    font-style: normal;
    font-weight: 600;
  }

  tr.divider {
    background-color: #fbfbfb;
  }

  .name {
    font-size: 1.1em;
    font-weight: 600;
  }

  .more-info {
    font-style: italic;
    color: grey;
  }

  .more-info i.mdi {
    font-size: 1.2em !important;
  }

  .more-info .v-icon__svg {
    height: 20px;
    width: 20px;
  }

  .welcome-cards-overlay {
    position: fixed;
    inset: 0;
    z-index: 1200;
    background: #fff;
    overflow: auto;
    padding: 16px 24px 48px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
  }

  .welcome-cards-overlay p {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
    letter-spacing: 0;
    text-transform: none;
  }

  .welcome-cards-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e3e0e8;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
  }

  .welcome-cards-toolbar .muted {
    color: #6b6570;
    font-size: 14px;
  }

  .welcome-cards-toolbar-actions {
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .welcome-cards-loading {
    font-size: 14px;
    color: #6b6570;
  }

  .welcome-cards-sheet {
    max-width: 560px;
  }

  .welcome-card {
    break-inside: avoid;
    page-break-inside: avoid;
    padding: 12px 0 14px;
    border-bottom: 1px dashed #d8d4ce;
  }

  .welcome-card-name {
    margin: 0 0 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 20px;
    font-weight: 700;
    color: #1c1b1f;
    line-height: 1.25;
  }

  .welcome-card-address,
  .welcome-card-note {
    margin: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 16px;
    line-height: 1.45;
    color: #1c1b1f;
  }

  .welcome-card-missing {
    margin: 0;
    font-size: 13px;
    font-style: italic;
    color: #9a6b1a;
  }

  .welcome-card-note {
    margin-top: 6px;
    color: #3d3a42;
  }

  .welcome-card-pieces {
    margin: 6px 0 0;
    padding: 0 0 0 1.25em;
    list-style: disc;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 16px;
    line-height: 1.45;
    color: #3d3a42;
  }

  .welcome-card-pieces li {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 16px;
    line-height: 1.45;
    color: #3d3a42;
    letter-spacing: 0;
    text-transform: none;
  }

  @media print {
    .no-print {
      display: none !important;
    }
    .iabf-header,
    .admin-sidebar,
    .iabf-footer,
    .dancer-footer {
      display: none !important;
    }
    .welcome-cards-overlay {
      position: static;
      inset: auto;
      overflow: visible;
      padding: 0;
    }
    .welcome-cards-sheet {
      max-width: none;
    }
  }
</style>

<script>
  import 'vue-good-table/dist/vue-good-table.css'
  import { VueGoodTable } from 'vue-good-table'
  import { computeCheckIn, computeCheckOut, extraNightsSummary, DEFAULT_CHECK_IN, DEFAULT_CHECK_OUT } from '@/utils/lodgingDates'
  import { IABF_DANCER_YEAR } from '@/config/iabf-event'
  import IabfPersonEmailIdentity from '@/components/iabf/IabfPersonEmailIdentity'
  import { uniqueEmails, ordinalYearLabel } from '@/utils/iabf-people'
  import { isOnsiteForRooming, isOnsiteOwnRoom, isOffsiteLodgingRoom, normalizeRoomNumber } from '@/utils/iabf-registration-package'
  import { formatMailingLines } from '@/config/iabf-mailing-address'
  import { corpsPiecesFromEnrollment } from '@/utils/iabf-festival-history'

  function csvEscape (val) {
    const s = String(val == null ? '' : val)
    return (s.includes(',') || s.includes('"') || s.includes('\n'))
      ? '"' + s.replace(/"/g, '""') + '"'
      : s
  }

  function compareRoomNumbers (a, b) {
    const na = normalizeRoomNumber(a)
    const nb = normalizeRoomNumber(b)
    if (!na && !nb) {
      return 0
    }
    if (!na) {
      return 1
    }
    if (!nb) {
      return -1
    }
    const ma = na.match(/^(\d+)/)
    const mb = nb.match(/^(\d+)/)
    const ia = ma ? parseInt(ma[1], 10) : NaN
    const ib = mb ? parseInt(mb[1], 10) : NaN
    if (Number.isFinite(ia) && Number.isFinite(ib) && ia !== ib) {
      return ia - ib
    }
    if (Number.isFinite(ia) && !Number.isFinite(ib)) {
      return -1
    }
    if (!Number.isFinite(ia) && Number.isFinite(ib)) {
      return 1
    }
    return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' })
  }

  function joinOccupantNames (people) {
    const firsts = (people || []).map(p => String(p.firstName || '').trim()).filter(Boolean)
    if (firsts.length <= 1) {
      const p = (people && people[0]) || {}
      return [p.firstName, p.lastName].filter(Boolean).join(' ')
    }
    if (firsts.length === 2) {
      return firsts[0] + ' and ' + firsts[1]
    }
    return firsts.slice(0, -1).join(', ') + ', and ' + firsts[firsts.length - 1]
  }

  function sharerPointsAtHost (sharer, host) {
    if (!sharer || !host) {
      return false
    }
    if (sharer.primaryRoomId && sharer.primaryRoomId !== 'None' && sharer.primaryRoomId === host.id) {
      return true
    }
    const text = String(sharer.roommateName || sharer.primaryRoommateName || '').trim().toLowerCase()
    if (!text) {
      return false
    }
    const full = [host.firstName, host.lastName].filter(Boolean).join(' ').trim().toLowerCase()
    const first = String(host.firstName || '').trim().toLowerCase()
    if (text === full || (first && text === first)) {
      return true
    }
    if (full && (text.indexOf(full) !== -1 || full.indexOf(text) !== -1)) {
      return true
    }
    return false
  }

  export default {
    components: {
      VueGoodTable,
      IabfPersonEmailIdentity
    },
    data () {
      return {
        emailIdentityOpen: false,
        emailIdentityRow: null,
        welcomeCardsOpen: false,
        welcomeCardsLoading: false,
        priorYearMealGuests: [],
        columns: [
          {
            label: 'Edit',
            field: 'edit'
          },
          {
            label: 'Dash',
            field: 'account'
          },
          {
            label: 'First Name',
            field: 'firstName'
          },
          {
            label: 'Last Name',
            field: 'lastName'
          },
          {
            label: 'Years',
            field: 'yearsAttendedCount',
            type: 'number',
            width: '80px'
          },
          {
            label: 'Email',
            field: 'email'
          },
          {
            label: 'Phone',
            field: 'phone'
          },
          {
            label: 'Travel details entered?',
            field: 'travelDetailsEntered'
          },
          {
            label: 'Diet',
            field: 'diet'
          },
          {
            label: 'Emergency Contact',
            field: 'emergencyContactName'
          },
          {
            label: 'Costume Ordered',
            field: 'costumeStatus'
          },
          {
            label: 'Registration Date',
            field: 'registrationDate'
          },
          {
            label: 'Registration Gift',
            field: 'registrationGift1'
          },
          {
            label: 'Registration Gift',
            field: 'registrationGift2'
          },
          {
            label: 'Registration Gift',
            field: 'registrationGift3'
          },
          {
            label: 'Rooming with us?',
            field: 'room'
          },
          {
            label: 'Installments',
            field: 'installments',
            hidden: true
          },
          {
            label: 'Deposit Received',
            field: 'initialPayment'
          },
          {
            label: 'Payment 1',
            field: 'payment1Due'
          },
          {
            label: 'Payment 2',
            field: 'payment2Due'
          },
          {
            label: 'Payment 3',
            field: 'payment3Due'
          },
          {
            label: 'Payment 4',
            field: 'payment4Due'
          },
          {
            label: 'Payment 5',
            field: 'payment5Due'
          },
          {
            label: 'Payment 6',
            field: 'payment6Due'
          },
          {
            label: 'Outstanding Balance',
            field: 'outstandingBalance',
            type: 'number'
          },
          {
            label: 'Payment 1 Status',
            field: 'payment1Status',
            hidden: true
          },
          {
            label: 'Payment 2 Status',
            field: 'payment2Status',
            hidden: true
          },
          {
            label: 'Payment 3 Status',
            field: 'payment3Status',
            hidden: true
          },
          {
            label: 'Payment 4 Status',
            field: 'payment4Status',
            hidden: true
          },
          {
            label: 'Payment 5 Status',
            field: 'payment5Status',
            hidden: true
          },
          {
            label: 'Payment 6 Status',
            field: 'payment6Status',
            hidden: true
          },
          {
            label: 'Edit',
            field: 'edit'
          },
          {
            label: 'Dash',
            field: 'account'
          }
        ],
        rows: [],
        facultyRows: [],
        emailsCopyNote: '',
        roomNumberDrafts: {},
        roomNumbersSaving: false,
        roomNumbersNote: '',
        removingId: ''
      }
    },
    created () {
      this.reloadDancers()
    },
    computed: {
      adminFestivalYear () {
        return this.$store.getters.adminFestivalYear
      },
      isArchiveYear () {
        return this.$store.getters.isAdminArchiveYear
      },
      isOperableYear () {
        return !this.isArchiveYear
      },
      year () {
        return String(this.adminFestivalYear)
      },
      emailIdentityPersonId () {
        const row = this.emailIdentityRow || {}
        return row.personId || (row.archiveOnly ? row.id : '') || ''
      },
      dancerEmails () {
        return uniqueEmails(this.rows.map(d => d.email))
      },
      roomTypeTally () {
        const order = [
          'Standard Dorm',
          'ADA Dorm',
          'Cottage',
          'Staying with another dancer',
          'Staying offsite'
        ]
        const counts = {}
        const people = this.rows || []
        people.forEach((p) => {
          const key = p.room || ''
          if (!key) {
            counts['No room yet'] = (counts['No room yet'] || 0) + 1
            return
          }
          counts[key] = (counts[key] || 0) + 1
        })
        const parts = []
        order.forEach((key) => {
          const n = counts[key] || 0
          if (!n) {
            return
          }
          parts.push(this.roomTypeLabel({ room: key }) + ' ' + n)
        })
        Object.keys(counts).forEach((key) => {
          if (order.indexOf(key) !== -1 || key === 'No room yet') {
            return
          }
          parts.push((key || 'Other') + ' ' + counts[key])
        })
        if (counts['No room yet']) {
          parts.push('No room yet ' + counts['No room yet'])
        }
        return parts.length ? parts.join(' · ') : 'None assigned yet'
      },
      yearCohortTally () {
        const counts = {}
        ;(this.rows || []).forEach((d) => {
          const n = Number(d.yearsAttendedCount) || 0
          if (n < 1) {
            return
          }
          counts[n] = (counts[n] || 0) + 1
        })
        const parts = Object.keys(counts)
          .map(Number)
          .sort((a, b) => a - b)
          .map((yearN) => {
            const count = counts[yearN]
            const ord = ordinalYearLabel(yearN)
            return count + ' ' + ord + '-year' + (count === 1 ? '' : 's')
          })
        return parts.length ? parts.join(' · ') : 'No years on file yet'
      },
      welcomeCardRows () {
        const pieces = this.$store.getters.loadedIABFPieces || []
        const cards = (this.rows || []).map((d) => this.buildWelcomeCard(d, pieces, this.priorYearMealGuests))
        cards.sort((a, b) => {
          const ln = String(a.lastName).toLowerCase().localeCompare(String(b.lastName).toLowerCase())
          if (ln !== 0) {
            return ln
          }
          return String(a.firstName).toLowerCase().localeCompare(String(b.firstName).toLowerCase())
        })
        return cards
      },
      facultyEmails () {
        return uniqueEmails(this.facultyRows.map(f => f.email))
      },
      allRosterEmails () {
        return uniqueEmails([...this.dancerEmails, ...this.facultyEmails])
      },
      onsiteEmails () {
        const dancer = (this.rows || [])
          .filter(d => isOnsiteForRooming(d.room))
          .map(d => d.email)
        const faculty = (this.facultyRows || [])
          .filter(f => isOnsiteForRooming(f.room))
          .map(f => f.email)
        return uniqueEmails([...dancer, ...faculty])
      },
      offsiteEmails () {
        const dancer = (this.rows || [])
          .filter(d => isOffsiteLodgingRoom(d.room))
          .map(d => d.email)
        const faculty = (this.facultyRows || [])
          .filter(f => isOffsiteLodgingRoom(f.room))
          .map(f => f.email)
        return uniqueEmails([...dancer, ...faculty])
      },
      onsiteRoomingPeople () {
        const pool = []
        this.rows.forEach((d) => {
          pool.push({
            ...d,
            kind: 'dancer',
            key: 'dancer:' + d.id,
            role: 'Dancer'
          })
        })
        this.facultyRows.forEach((f) => {
          pool.push({
            ...f,
            kind: 'faculty',
            key: 'faculty:' + f.id,
            role: 'Faculty'
          })
        })
        const holders = pool.filter(p => isOnsiteOwnRoom(p.room))
        const sharers = pool.filter(p => p.room === 'Staying with another dancer')
        const claimed = {}
        const people = holders.map((host) => {
          const withMe = sharers.filter((s) => {
            if (claimed[s.key]) {
              return false
            }
            if (!sharerPointsAtHost(s, host)) {
              return false
            }
            claimed[s.key] = true
            return true
          })
          const occupants = [host].concat(withMe)
          return {
            key: host.key,
            kind: host.kind,
            id: host.id,
            name: joinOccupantNames(occupants),
            lastName: host.lastName || '',
            firstName: host.firstName || '',
            role: host.role,
            roomType: this.roomTypeLabel(host),
            roomNumber: host.roomNumber || '',
            sharers: withMe.map(s => ({ kind: s.kind, id: s.id, key: s.key }))
          }
        })
        people.sort((a, b) => {
          const byRoom = compareRoomNumbers(a.roomNumber, b.roomNumber)
          if (byRoom !== 0) {
            return byRoom
          }
          const ln = String(a.lastName).toLowerCase().localeCompare(String(b.lastName).toLowerCase())
          if (ln !== 0) {
            return ln
          }
          return String(a.firstName).toLowerCase().localeCompare(String(b.firstName).toLowerCase())
        })
        return people
      },
      onsiteRoomsAssignedCount () {
        return this.onsiteRoomingPeople.filter(p => normalizeRoomNumber(this.roomNumberFor(p))).length
      },
      roomNumbersDirty () {
        return this.onsiteRoomingPeople.some(p => {
          return normalizeRoomNumber(this.roomNumberFor(p)) !== normalizeRoomNumber(p.roomNumber)
        })
      },
      paymentTotals () {
        // For each payment slot 1-6: sum across all loaded rows. Past
        // payments report "Collected" (sum where status === 'Paid');
        // today and future report "Expected" (sum where status != 'N/A').
        // Plus: each dancer's outstandingBalance (unpaid costume + guest
        // meal/banquet) is added to whichever Expected column is their
        // next unpaid scheduled payment — so the forecast captures the
        // bump that will land on that invoice.
        if (this.isArchiveYear) {
          return []
        }
        const paymentMonths = [0, 1, 2, 3, 4, 5]
        const year = this.adminFestivalYear || IABF_DANCER_YEAR
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const out = []
        for (let i = 1; i <= 6; i++) {
          const dueDate = new Date(year, paymentMonths[i - 1], 5)
          dueDate.setHours(0, 0, 0, 0)
          const isPast = dueDate < today
          let total = 0
          for (const row of this.rows) {
            const due = parseFloat(row[`payment${i}Due`]) || 0
            const status = row[`payment${i}Status`]
            if (!status || status === 'N/A') continue
            if (isPast) {
              if (status === 'Paid') total += due
            } else {
              total += due
            }
          }
          out.push({ mode: isPast ? 'Collected' : 'Expected', total, dueDate })
        }

        // Add each dancer's outstanding to their next-unpaid Expected
        // column. Past (overdue) columns stay "Collected" untouched —
        // adding forecast outstanding into a collected-revenue number
        // would muddy the meaning of that header.
        for (const row of this.rows) {
          const outstanding = parseFloat(row.outstandingBalance) || 0
          if (outstanding <= 0) continue
          for (let i = 1; i <= 6; i++) {
            const status = row[`payment${i}Status`]
            if (!status || status === 'N/A') continue
            if (status === 'Paid') continue
            if (out[i - 1].mode !== 'Expected') break
            out[i - 1].total += outstanding
            break
          }
        }

        return out.map(o => ({ mode: o.mode, total: Math.round(o.total) }))
      },
      displayColumns () {
        if (this.isArchiveYear) {
          // Slim archive roster — name / contact only
          return [
            { label: 'Link Emails', field: 'emailIdentity' },
            { label: 'First Name', field: 'firstName', filterOptions: { enabled: true } },
            { label: 'Last Name', field: 'lastName', filterOptions: { enabled: true } },
            { label: 'Years', field: 'yearsAttendedCount', type: 'number', width: '80px' },
            { label: 'Email', field: 'email', filterOptions: { enabled: true } },
            { label: 'Phone', field: 'phone', filterOptions: { enabled: true } },
            { label: '', field: 'remove', sortable: false }
          ]
        }
        // Inject the per-column "Collected $X" / "Expected $X" line
        // into each payment column's label without touching the base
        // columns config.
        const fmt = n => '$' + n.toLocaleString()
        return this.columns.map(col => {
          const match = (col.field || '').match(/^payment([1-6])Due$/)
          if (!match) return col
          const totals = this.paymentTotals[parseInt(match[1], 10) - 1]
          if (!totals) return col
          return {
            ...col,
            label: `${col.label} — ${totals.mode} ${fmt(totals.total)}`
          }
        })
      },
    },
    methods: {
      masqueradeAs (dancerId) {
        const dancer = (this.$store.getters.loadedIABFDancers || []).find(d => d.id === dancerId)
        this.$store.commit('setMasqueradeDancer', dancer || dancerId)
        this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
        this.$router.push('/')
      },
      openEmailIdentity (row) {
        this.emailIdentityRow = row
        this.emailIdentityOpen = true
      },
      async removeFromArchiveYear (row) {
        if (!row || !this.isArchiveYear || this.removingId) {
          return
        }
        const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.email || 'this person'
        const year = this.adminFestivalYear
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          'Remove ' + name + ' from IABF ' + year + '?\n\nThey will no longer count as having attended ' + year + '. Other years stay. This does not delete their person record.'
        )
        if (!ok) {
          return
        }
        this.removingId = row.id
        try {
          await this.$store.dispatch('removeDancerFromArchiveYear', row)
          this.rows = this.rows.filter(r => r.id !== row.id)
        } catch (e) {
          console.error('remove from archive year failed', e)
          // eslint-disable-next-line no-alert
          window.alert('Could not remove them: ' + (e && e.message ? e.message : e))
        }
        this.removingId = ''
      },
      onEmailIdentityUpdated (result) {
        if (!this.emailIdentityRow || !result) {
          return
        }
        if (result.email) {
          this.$set(this.emailIdentityRow, 'email', result.email)
        }
        this.$store.dispatch('loadIABFDancers')
      },
      buildRow (dancer) {
        const totalCost = Number(dancer.costumeTotalCost) || 0
        let costumeStatus = 'No'
        if (dancer.costumeOrderCompleted && (dancer.costumeOrderPaid || totalCost === 0)) costumeStatus = 'Yes - Paid'
        else if (dancer.costumeOrderCompleted) costumeStatus = 'Yes - Unpaid'

        const costumeBalance = (dancer.costumeOrderCompleted && !dancer.costumeOrderPaid)
          ? totalCost
          : 0

        const guestBalance = this.$store.getters.allGuestRegistrations
          .filter(g => g.dancerId === dancer.id && g.paymentStatus !== 'paid')
          .reduce((sum, g) => sum + (Number(g.totalCost) || 0), 0)

        return { ...dancer, costumeStatus, outstandingBalance: costumeBalance + guestBalance }
      },
      // Check-in/out for an onsite stay, derived from the festival default plus any
      // extra-night requests on the person's travel-details doc. Pending requests
      // are counted in the dates and surfaced in the "Extra Nights" column.
      computeStay (travelDetails) {
        return {
          checkIn: computeCheckIn(travelDetails),
          checkOut: computeCheckOut(travelDetails),
          extraNights: extraNightsSummary(travelDetails)
        }
      },
      roomTypeLabel (dancer) {
        const r = dancer.room
        if (r === 'Staying with another dancer') return 'Shared room (under roommate)'
        if (r === 'Yes') return 'Room confirmed (type not set)'
        if (r === 'ADA Dorm') return 'Larger Dorm'
        return r || ''
      },
      roommateName (dancer, dancerById) {
        // Public registration stores free-text name of who holds the onsite room
        const freeText = dancer.roommateName || dancer.primaryRoommateName || ''
        if (freeText) return freeText
        if (!dancer.primaryRoomId || dancer.primaryRoomId === 'None') return ''
        const mate = dancerById[dancer.primaryRoomId]
        return mate ? [mate.firstName, mate.lastName].filter(Boolean).join(' ') : ''
      },
      roomNumberFor (person) {
        if (!person) {
          return ''
        }
        if (Object.prototype.hasOwnProperty.call(this.roomNumberDrafts, person.key)) {
          return this.roomNumberDrafts[person.key]
        }
        return person.roomNumber || ''
      },
      onRoomNumberInput (person, ev) {
        const next = ev && ev.target ? ev.target.value : ''
        this.$set(this.roomNumberDrafts, person.key, next)
        this.roomNumbersNote = ''
      },
      async persistRoomNumber (kind, id, next) {
        if (kind === 'faculty') {
          await this.$store.dispatch('updateIabfFaculty', { id, roomNumber: next })
          const row = this.facultyRows.find(f => f.id === id)
          if (row) {
            this.$set(row, 'roomNumber', next)
          }
        } else {
          await this.$store.dispatch('updateDancerRoomNumber', { id, roomNumber: next })
          const row = this.rows.find(d => d.id === id)
          if (row) {
            this.$set(row, 'roomNumber', next)
          }
        }
      },
      async saveRoomNumbers () {
        const dirty = this.onsiteRoomingPeople.filter(p => {
          return normalizeRoomNumber(this.roomNumberFor(p)) !== normalizeRoomNumber(p.roomNumber)
        })
        if (!dirty.length) {
          return
        }
        this.roomNumbersSaving = true
        this.roomNumbersNote = ''
        try {
          for (const person of dirty) {
            const next = normalizeRoomNumber(this.roomNumberFor(person))
            await this.persistRoomNumber(person.kind, person.id, next)
            const sharers = person.sharers || []
            for (const s of sharers) {
              await this.persistRoomNumber(s.kind, s.id, next)
            }
            this.$delete(this.roomNumberDrafts, person.key)
          }
          this.roomNumbersNote = 'Saved — list is in room-number order.'
          setTimeout(() => { this.roomNumbersNote = '' }, 3200)
        } catch (e) {
          this.roomNumbersNote = 'Could not save: ' + ((e && e.message) || e)
        }
        this.roomNumbersSaving = false
      },
      downloadCsvFile (filename, headers, rows) {
        const csv = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      },
      rosterEmailsFor (kind) {
        if (kind === 'dancers') {
          return this.dancerEmails
        }
        if (kind === 'faculty') {
          return this.facultyEmails
        }
        if (kind === 'onsite') {
          return this.onsiteEmails
        }
        if (kind === 'offsite') {
          return this.offsiteEmails
        }
        return this.allRosterEmails
      },
      async copyRosterEmails (kind) {
        const emails = this.rosterEmailsFor(kind)
        const text = emails.join('\n')
        if (!text) {
          return
        }
        const label = kind === 'dancers'
          ? 'dancer'
          : kind === 'faculty'
            ? 'faculty'
            : kind === 'onsite'
              ? 'onsite (dancers + faculty)'
              : kind === 'offsite'
                ? 'offsite (dancers + faculty)'
                : 'dancer + faculty'
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text)
          } else {
            const ta = document.createElement('textarea')
            ta.value = text
            ta.setAttribute('readonly', '')
            ta.style.position = 'fixed'
            ta.style.left = '-9999px'
            document.body.appendChild(ta)
            ta.select()
            document.execCommand('copy')
            document.body.removeChild(ta)
          }
          const noun = emails.length === 1 ? 'email' : 'emails'
          this.emailsCopyNote = `✓ Copied ${emails.length} ${label} ${noun}`
        } catch (e) {
          // eslint-disable-next-line no-alert
          window.prompt(`Copy these ${emails.length} email(s):`, text)
          this.emailsCopyNote = 'Copy failed — pasted in prompt'
        }
        setTimeout(() => { this.emailsCopyNote = '' }, 2400)
      },
      // Rooming list for onsite stays at the Atlantic Center for the Arts:
      // onsite dancers and faculty, plus guests who chose to stay at the
      // retreat with their host.
      downloadRoomingList () {
        const dancers = this.$store.getters.loadedIABFDancers
        const faculty = this.facultyRows || []
        const guests = this.$store.getters.allGuestRegistrations || []
        const travelDetails = this.$store.getters.loadedAllTravelDetails || []

        const travelByPerson = {}
        for (const td of travelDetails) {
          if (td.personId) travelByPerson[td.personId] = td
        }
        const dancerById = {}
        for (const d of dancers) {
          dancerById[d.id] = d
        }
        const facultyById = {}
        for (const f of faculty) {
          facultyById[f.id] = f
        }
        const fullName = p => [p.firstName, p.lastName].filter(Boolean).join(' ')
        const roomNum = p => normalizeRoomNumber(p && p.roomNumber)

        const retreatGuestsByHost = {}
        for (const g of guests) {
          if (g.lodging !== 'retreat') continue
          const key = g.dancerId || '__none__'
          if (!retreatGuestsByHost[key]) retreatGuestsByHost[key] = []
          retreatGuestsByHost[key].push(g)
        }

        const onsitePeople = []
        for (const d of dancers) {
          if (!isOnsiteForRooming(d.room)) continue
          onsitePeople.push({ person: d, role: 'Dancer' })
        }
        for (const f of faculty) {
          if (!isOnsiteForRooming(f.room)) continue
          onsitePeople.push({ person: f, role: 'Faculty' })
        }
        onsitePeople.sort((a, b) => {
          const ln = (a.person.lastName || '').toLowerCase().localeCompare((b.person.lastName || '').toLowerCase())
          if (ln !== 0) return ln
          return (a.person.firstName || '').toLowerCase().localeCompare((b.person.firstName || '').toLowerCase())
        })

        const rows = []
        const handledHosts = new Set()
        for (const item of onsitePeople) {
          const d = item.person
          const stay = this.computeStay(travelByPerson[d.id])
          const number = roomNum(d)
          rows.push([
            fullName(d), item.role, this.roomTypeLabel(d), this.roommateName(d, dancerById),
            number, stay.checkIn, stay.checkOut, stay.extraNights
          ])
          handledHosts.add(d.id)
          for (const g of (retreatGuestsByHost[d.id] || [])) {
            rows.push([
              g.guestName || '', item.role === 'Faculty' ? 'Faculty Guest' : 'Guest',
              `With ${fullName(d)}`, fullName(d),
              number, stay.checkIn, stay.checkOut, stay.extraNights
            ])
          }
        }

        // Retreat guests whose host isn't an onsite dancer/faculty (e.g. a
        // guest staying onsite while their dancer is offsite).
        for (const hostId of Object.keys(retreatGuestsByHost)) {
          if (handledHosts.has(hostId)) continue
          const host = dancerById[hostId] || facultyById[hostId] || null
          const hostName = host ? fullName(host) : ''
          const number = roomNum(host)
          for (const g of retreatGuestsByHost[hostId]) {
            const role = g.type === 'faculty_guest' || (host && host.isFaculty) ? 'Faculty Guest' : 'Guest'
            rows.push([
              g.guestName || '', role, (hostName || g.dancerName) ? `With ${hostName || g.dancerName}` : '(with host)',
              hostName || g.dancerName || '', number, DEFAULT_CHECK_IN, DEFAULT_CHECK_OUT, ''
            ])
          }
        }

        const headers = ['Name', 'Role', 'Room Type', 'Roommate', 'Room Number', 'Check-in', 'Check-out', 'Extra Nights']
        this.downloadCsvFile('iabf-' + this.year + '-rooming-list.csv', headers, rows)
      },
      downloadNametagsCsv () {
        // One row per nametag: every faculty, every guest, every dancer.
        // Guests get a column showing who they're attached to (their host
        // dancer for dancer_guests, the faculty member for faculty_guests).
        // Output is sorted by Type then Name so cutting/printing is tidy.
        const allGuests = this.$store.getters.allGuestRegistrations || []
        const dancersById = {}
        this.rows.forEach(r => { dancersById[r.id] = `${r.firstName || ''} ${r.lastName || ''}`.trim() })
        const facultyById = {}
        allGuests.forEach(g => { if (g.type === 'faculty') facultyById[g.id] = g.guestName || '' })

        const out = []
        // Dancers
        for (const d of this.rows) {
          const name = `${d.firstName || ''} ${d.lastName || ''}`.trim()
          if (!name) continue
          out.push({ name, type: 'Dancer', attachedTo: '' })
        }
        // Faculty + guests
        for (const g of allGuests) {
          const name = (g.guestName || '').trim()
          if (!name) continue
          if (g.type === 'faculty') {
            out.push({ name, type: 'Faculty', attachedTo: '' })
          } else if (g.type === 'faculty_guest') {
            const host = facultyById[g.dancerId] || g.dancerName || ''
            out.push({ name, type: 'Guest', attachedTo: host })
          } else {
            // Default: dancer's guest (no type or type === 'guest')
            const host = dancersById[g.dancerId] || g.dancerName || ''
            out.push({ name, type: 'Guest', attachedTo: host })
          }
        }
        out.sort((a, b) => {
          const typeRank = { Dancer: 0, Faculty: 1, Guest: 2 }
          if (typeRank[a.type] !== typeRank[b.type]) return typeRank[a.type] - typeRank[b.type]
          return a.name.localeCompare(b.name)
        })

        const headers = ['Name', 'Type', 'Attached To']
        const rows = out.map(r => [r.name, r.type, r.attachedTo])
        this.downloadCsvFile('iabf-2026-nametags.csv', headers, rows)
      },
      async openWelcomeCards () {
        this.welcomeCardsOpen = true
        this.welcomeCardsLoading = true
        const priorYear = Number(this.adminFestivalYear) - 1
        try {
          const [guests] = await Promise.all([
            this.$store.dispatch('loadPriorYearMealGuests', priorYear),
            this.$store.dispatch('loadIABFPieces', { useAdminYear: true })
          ])
          this.priorYearMealGuests = Array.isArray(guests) ? guests : []
        } catch (e) {
          console.warn('load welcome-card extras failed', e)
          this.priorYearMealGuests = []
        }
        this.welcomeCardsLoading = false
      },
      printWelcomeCards () {
        window.print()
      },
      pieceIdsOn (piece) {
        const ids = []
        const raw = Array.isArray(piece && piece.dancers) ? piece.dancers : []
        raw.forEach((d) => {
          if (typeof d === 'string' && d) {
            ids.push(d)
          } else if (d && d.id) {
            ids.push(d.id)
          }
        })
        if (piece && piece.submittedByDancerId) {
          ids.push(piece.submittedByDancerId)
        }
        return ids
      },
      showcaseTitlesFor (dancer, pieces) {
        const idSet = {}
        ;[dancer.id, dancer.personId].filter(Boolean).forEach((id) => {
          idSet[id] = true
        })
        const titles = []
        const seen = {}
        ;(pieces || []).forEach((p) => {
          if (p && p.canceledByDancer) {
            return
          }
          if ((p.approvalStatus || 'approved') !== 'approved') {
            return
          }
          const onPiece = this.pieceIdsOn(p).some((id) => idSet[id])
          if (!onPiece) {
            return
          }
          const title = String((p && (p.programTitle || p.title)) || '').trim()
          if (!title || seen[title]) {
            return
          }
          seen[title] = true
          titles.push(title)
        })
        return titles
      },
      priorMealGuestHadMeals (guest) {
        if (!guest || guest.type === 'faculty') {
          return false
        }
        if (guest.allMeals || guest.awardsAfterparty) {
          return true
        }
        return Array.isArray(guest.meals) && guest.meals.length > 0
      },
      priorMealGuestNamesFor (dancer, guests) {
        const personId = dancer.personId || ''
        const email = String(dancer.email || '').trim().toLowerCase()
        const names = []
        const seen = {}
        ;(guests || []).forEach((g) => {
          if (!this.priorMealGuestHadMeals(g)) {
            return
          }
          const name = String(g.guestName || '').trim()
          if (!name || seen[name.toLowerCase()]) {
            return
          }
          const samePerson = personId && g.hostPersonId && g.hostPersonId === personId
          const sameEmail = email && g.hostEmail && g.hostEmail === email
          const sameEnrollment = g.dancerId && g.dancerId === dancer.id
          if (!samePerson && !sameEmail && !sameEnrollment) {
            return
          }
          seen[name.toLowerCase()] = true
          names.push(name)
        })
        return names
      },
      buildWelcomeCard (dancer, pieces, priorGuests) {
        const firstName = String(dancer.firstName || '').trim()
        const lastName = String(dancer.lastName || '').trim()
        const name = [firstName, lastName].filter(Boolean).join(' ')
        const addressLines = formatMailingLines({
          addressStreet: dancer.addressStreet,
          addressStreet2: dancer.addressStreet2,
          addressCity: dancer.addressCity,
          addressState: dancer.addressState,
          addressZip: dancer.addressZip,
          addressCountry: dancer.addressCountry
        })
        const yearN = Number(dancer.yearsAttendedCount) || 0
        const count = yearN > 0 ? yearN : 1
        const ord = ordinalYearLabel(count)
        const yearsLine = ord ? (ord + ' year at IABF') : 'Years unknown'
        const showcase = this.showcaseTitlesFor(dancer, pieces)
        const corps = corpsPiecesFromEnrollment(dancer).map((p) => p.name).filter(Boolean)
        const pieceNames = []
        const seen = {}
        showcase.concat(corps).forEach((t) => {
          if (!t || seen[t]) {
            return
          }
          seen[t] = true
          pieceNames.push(t)
        })
        return {
          id: dancer.id,
          firstName,
          lastName,
          name: name || 'Dancer',
          addressLines,
          yearsLine,
          pieceNames,
          priorGuestNames: this.priorMealGuestNamesFor(dancer, priorGuests)
        }
      },
      downloadWelcomeCardsCsv () {
        const headers = ['Name', 'Address', 'Years', 'Pieces', 'Last year meals']
        const rows = this.welcomeCardRows.map((c) => [
          c.name,
          c.addressLines.length ? c.addressLines.join('\n') : '',
          c.yearsLine,
          c.pieceNames.length ? c.pieceNames.join('\n') : 'No pieces registered yet',
          c.priorGuestNames.join('\n')
        ])
        this.downloadCsvFile('iabf-' + this.year + '-welcome-cards.csv', headers, rows)
      },
      reloadDancers () {
        this.rows = []
        this.facultyRows = []
        this.roomNumberDrafts = {}
        this.roomNumbersNote = ''
        const loadDancers = this.$store.dispatch('loadIABFDancers')
        const loadFaculty = this.$store.dispatch('loadIabfFaculty').then((list) => {
          this.facultyRows = list || []
        }).catch((e) => {
          console.warn('load faculty emails failed', e)
          this.facultyRows = []
        })
        const loaders = [loadDancers, loadFaculty]
        if (this.isOperableYear) {
          loaders.push(
            this.$store.dispatch('loadAllGuestRegistrations'),
            this.$store.dispatch('loadAllTravelDetails')
          )
        }
        return Promise.all(loaders).then(() => {
          this.$store.getters.loadedIABFDancers.forEach((dancer) => {
            if (dancer && dancer.registrationStatus === 'pending_payment') {
              return
            }
            if (this.isArchiveYear) {
              this.rows.push(dancer)
            } else {
              this.rows.push(this.buildRow(dancer))
            }
          })
        })
      },
      formatPaymentStatus (amount, status) {
        if (status === 'Overdue') {
          return 'OVERDUE - $' + amount
        } else if (status === 'Paid') {
          return 'Paid - $' + amount
        } else if (status === 'Upcoming') {
          return '$' + amount
        } else {
          return '$' + amount
        }
      },
      payment1Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment1Due'], formattedRow['payment1Status'])
        }
      },
      payment2Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment2Due'], formattedRow['payment2Status'])
        }
      },
      payment3Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment3Due'], formattedRow['payment3Status'])
        }
      },
      payment4Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment4Due'], formattedRow['payment4Status'])
        }
      },
      payment5Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment5Due'], formattedRow['payment5Status'])
        }
      },
      payment6Display (formattedRow) {
        if (formattedRow['installments'] === false) {
          return ''
        } else {
          return this.formatPaymentStatus(formattedRow['payment6Due'], formattedRow['payment6Status'])
        }
      }
    }
  }
</script>
